import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  CircularProgress,
  LinearProgress,
  Tab,
  Tabs,
} from "@mui/material";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from "react";
import { TAnalyticView } from "../../shared/types/analyticView.type";
import { isEmpty, toNumber } from "lodash";
import { useAnalyticTable } from "../../hooks/useAnalyticTable";
import { AnalyticsData } from "../../shared/types/analyticData.type";
import Datatable from "./Datatable";
import { TAnalyticData } from "../../pages/analytics";
import { TAnalyticParams } from "../../shared/types/analyticParameters.type";
import { VIEW_PREFIX } from "../../hooks/useAsAdmin";
import { useQueryClient } from "react-query";
import { Add } from "@mui/icons-material";

type TabConfig = {
  analyticViews: Partial<TAnalyticView>[];
  setView: (view: TAnalyticView,params:TAnalyticParams[]) => void;
  OnCreateNewView: () => void;
  loading: boolean;
  dataLoading: boolean;
  analyticsData?: AnalyticsData;
  view: TAnalyticView | null;
  updateFilter: (key: keyof TAnalyticData, value: any) => void;
  updateName: (name: string) => void;
  toggleDeleteModal: (value: boolean) => void;
  openNewParamsModal: () => void;
  isDeleting: boolean;
  isNameUpdating: boolean;
  isEditEnabled: boolean;
  selectedParams: TAnalyticParams[];
  updateSelectedParams: (items: TAnalyticParams[]) => void;
  disableEdit?: boolean;
  moreThan1000: boolean;
  isInitialLoad: boolean;
  selectedHorse?: number;
};

export type ConfigTabsRef = {
  fetchView: (id: number, forceRefresh?: boolean) => void;
};

const ConfigTabs = forwardRef<ConfigTabsRef, TabConfig>(function ConfigTabs(
  {
    selectedHorse,
    isInitialLoad,
    moreThan1000,
    disableEdit,
    isEditEnabled,
    updateSelectedParams,
    selectedParams,
    analyticViews,
    setView,
    loading,
    view,
    analyticsData,
    updateFilter,
    dataLoading,
    updateName,
    toggleDeleteModal,
    isDeleting,
    isNameUpdating,
    openNewParamsModal,
    OnCreateNewView,
  },
  ref
) {
  const queryClient = useQueryClient();

  // State
  const [value, setValue] = React.useState<string>("");

  // Hooks
  const { getViewById } = useAnalyticTable(false);

  // Helper function to process and set the data
  const processViewData = useCallback(
    (data: TAnalyticView) => {
      const insigtParams = data.analytics_view_insight_parameters.map(
        (e) => e.insight_parameter
      );

      const blacklist = ["training.date", "horse.name"];
      const filteredInsight = insigtParams.filter(
        (item) => !blacklist.includes(item.name)
      );

      setView(data,filteredInsight);
    },
    []
  );

  // Fetch view function with optional forceRefresh parameter
  const fetchView = useCallback(
    (id: number, forceRefresh = false) => {
      const stringId = String(id);
      setValue(stringId);
      const queryKey = ["view", id];

      const cachedData = queryClient.getQueryData(queryKey);

      if (cachedData && !forceRefresh) {
        processViewData(cachedData as TAnalyticView);
      } else {
        getViewById
          .mutateAsync({ id, isInitialLoad, selectedHorse })
          .then((result) => {
            queryClient.setQueryData(queryKey, result.data);
            processViewData(result.data);
          })
          .catch((err) => {
            console.error("Failed to fetch view:", err);
          });
      }
    },
    [isInitialLoad, selectedHorse]
  );

  // Sync internal state if active view prop changes externally
  useEffect(() => {
    if (view?.id) {
      setValue(String(view.id));
    }
  }, [view?.id]);

  // Expose fetchView through ref
  useImperativeHandle(
    ref,
    () => ({
      fetchView,
    }),
    [fetchView]
  );

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (newValue === "add-new-tab") {
      OnCreateNewView();
      return;
    }
    fetchView(toNumber(newValue));
  };

  // Keep active tab valid when analyticViews list changes (e.g., after delete)
  useEffect(() => {
    if (isEmpty(analyticViews)) return;

    const currentTabExists = analyticViews.some(
      (v) => String(v.id) === value
    );

    if (!value || !currentTabExists) {
      const firstId = analyticViews[0]?.id;
      if (firstId) {
        fetchView(toNumber(firstId));
      }
    }
  }, [analyticViews, value]);

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      {getViewById.isLoading && (
        <Box mb={2} display={"flex"} justifyContent={"center"}>
          <CircularProgress />
        </Box>
      )}

      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            variant="scrollable"
            value={value}
            onChange={handleChange}
            aria-label="view tabs"
          >
            {analyticViews.map((v) => (
              <Tab
                disabled={getViewById.isLoading}
                sx={{
                  margin: "0px !important",
                  borderRadius: 0,
                  paddingInline: "20px",
                  backgroundColor: v.name?.includes(VIEW_PREFIX)
                    ? "rgba(155, 20, 26, 0.3)"
                    : "inherit",
                }}
                key={v.id}
                label={v.name}
                value={String(v.id)}
              />
            ))}
            <Tab
              icon={<Add sx={{ color: "common.white" }} />}
              value="add-new-tab"
              aria-label="add new tab"
              sx={{
                minWidth: "auto",
                minHeight: "38px",
                paddingInline: "19px",
                borderTopRightRadius: 16,
                backgroundColor: "primary.main",
                boxShadow: `
                  0px -3px 8px rgba(0, 0, 0, 0.15),
                  2px 0px 4px rgba(0, 0, 0, 0.06)
                `,
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:active": {
                  transform: "translateY(0px)",
                },
              }}
            />
          </Tabs>
        </Box>
        <TabPanel value={value}>
          {dataLoading && <LinearProgress />}
          <Datatable
            updateSelectedParams={updateSelectedParams}
            isDeleting={isDeleting}
            toggleDeleteModal={toggleDeleteModal}
            updateName={updateName}
            updateFilter={updateFilter}
            loading={false}
            view={view}
            isNameUpdating={isNameUpdating}
            analyticsData={analyticsData}
            openNewParamsModal={openNewParamsModal}
            selectedParams={selectedParams}
            isEditEnabled={isEditEnabled}
            disableEdit={disableEdit}
            moreThan1000={moreThan1000}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
});

export default ConfigTabs;